import React from 'react';

import { Box, Display, Flex, Heading, Image, Paragraph } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import type { HeadingVariant } from '@rover/react-lib/src/components/lists/Carousel/components/Header';
import UnorderedList, {
  Icons,
} from '@rover/react-lib/src/components/lists/UnorderedList/UnorderedList';

import defaultServiceBenefitsImage from './images/three_dogs_and_cloud.png';
import defaultServiceBenefitsMobileImage from './images/two_dogs.png';
import {
  DESKTOP_MARGIN_Y,
  MOBILE_MARGIN_Y,
  SERVICE_LANDING_SECTION_MARGIN_Y,
} from './ServiceLandingSection';

export type ServiceBenefitsImageSrcSet = {
  desktop: {
    src: string;
    width: number;
    height: number;
  };
  mobile: {
    src: string;
    width: number;
    height: number;
  };
};

type ServiceBenefitsProps = {
  serviceBenefitsImageSrcSet?: ServiceBenefitsImageSrcSet;
  title?: React.ReactNode;
  headingVariant?: HeadingVariant;
  /** Props passed to both desktop and mobile Image components (e.g. sx for borderRadius) */
  imageProps?: Partial<React.ComponentProps<typeof Image>>;
  /** Gap between image and text columns on desktop */
  columnGap?: string;
  /** Width of the image column on desktop (e.g. '585px' or '50%') */
  imageWidth?: string;
  /** Background color for the section, responsive array supported */
  background?: React.ComponentProps<typeof Section>['background'];
  children?: React.ReactNode;
};

type ChildrenProps = {
  children?: React.ReactNode;
};

type ListItemProps = React.ComponentProps<typeof Box> & {
  title: React.ReactNode;
  children: React.ReactNode;
};

const MAX_TEXT_WIDTH = '558px';

const SERVICE_BENEFITS_IMAGES: ServiceBenefitsImageSrcSet = {
  desktop: {
    src: defaultServiceBenefitsImage,
    width: 589,
    height: 493,
  },
  mobile: {
    src: defaultServiceBenefitsMobileImage,
    width: 242,
    height: 189,
  },
};

const ServiceBenefits = ({
  serviceBenefitsImageSrcSet = SERVICE_BENEFITS_IMAGES,
  title,
  headingVariant = 'display',
  imageProps,
  columnGap = '6x',
  imageWidth = '50%',
  background = ['secondary', 'primary'],
  children,
}: ServiceBenefitsProps): JSX.Element => {
  const { desktop, mobile } = serviceBenefitsImageSrcSet;
  return (
    <>
      {/* Mobile: Image appears on top of the section */}
      {/* and service landing section top margins are applied */}
      <Flex display={['flex', 'none']} justifyContent="center" width="100%" mt={MOBILE_MARGIN_Y}>
        <Box
          width={`${mobile.width}px`}
          maxWidth="80%"
          sx={{ aspectRatio: mobile.width / mobile.height }}
          data-testid="service-benefits-image-mobile"
        >
          <Image src={mobile.src} description="" width="100%" height="100%" {...imageProps} />
        </Box>
      </Flex>

      <Section
        mt={['0x', DESKTOP_MARGIN_Y]} // on mobile, margins are applied to the image container above
        mb={SERVICE_LANDING_SECTION_MARGIN_Y}
        data-testid="service-benefits-container"
        background={background}
        position="relative"
      >
        <Flex
          data-testid="service-benefits-two-column-layout"
          flexDirection={['column', 'row']}
          alignItems="center"
          justifyContent="center"
          sx={{
            gap: ['0', columnGap],
          }}
          width="100%"
        >
          <Box
            display={['none', 'none', 'block']}
            maxWidth={`${desktop.width}px`}
            flexBasis={['0%', '40%', imageWidth]}
            alignSelf="stretch"
            data-testid="service-benefits-image-desktop"
          >
            <Image
              src={desktop.src}
              width="100%"
              height="100%"
              description=""
              resizeMethod="contain"
              {...imageProps}
            />
          </Box>

          <Box
            px={['5x', '0x']}
            pt={['6x', '0x']}
            flexBasis={['100%', '60%']}
            flex={['none', 'none', '1']}
          >
            {title &&
              (headingVariant === 'display' ? (
                <Display webProps={{ as: 'h3', responsive: true }} size="400" textAlign="left">
                  {title}
                </Display>
              ) : (
                <Heading webProps={{ as: 'h3', responsive: true }} size="400" textAlign="left">
                  {title}
                </Heading>
              ))}
            {children}
          </Box>
        </Flex>
      </Section>
    </>
  );
};

ServiceBenefits.List = (props: ChildrenProps): JSX.Element => (
  <Flex width="100%" justifyContent="center">
    <UnorderedList icon={Icons.CheckMark} width="100%" maxWidth={MAX_TEXT_WIDTH}>
      {props.children}
    </UnorderedList>
  </Flex>
);

ServiceBenefits.ListItem = ({ title, children, ...boxProps }: ListItemProps): JSX.Element => (
  <Box marginBottom="8x" {...boxProps}>
    <Heading mb="1x">{title}</Heading>
    <Paragraph textColor="tertiary">{children}</Paragraph>
  </Box>
);

export default ServiceBenefits;
